import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ArrowLeft, Pencil, Trash2, CheckCircle2, XCircle, Building2, User, Clock, Shield, Plus, X, Play, Square, Package, Users, FileText } from "lucide-react"
import { useServiceTicket, useDeleteServiceTicket, useChangeTicketPhase, useResolveTicket, useCloseTicket } from "@/hooks/use-service-tickets"
import { RecordNavigation } from "@/components/record-navigation"
import { useTicketType } from "@/hooks/use-ticket-types"
import { useTicketEquipment, useAddTicketEquipment, useRemoveTicketEquipment, useEquipment } from "@/hooks/use-equipment"
import { useTicketWorkers, useAddTicketWorker, useRemoveTicketWorker, useTicketTimeEntries, useCreateTimeEntry, useStopTimeEntry, useDeleteTimeEntry, useTicketParts, useAddTicketPart, useDeleteTicketPart } from "@/hooks/use-service-workers"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ActivityTimeline } from "@/components/activity-timeline"
import { NotesTab } from "@/components/notes-tab"
import { useEntityRelated } from "@/hooks/use-entity-related"
import { formatDate } from "@/lib/utils"
import { toast } from "sonner"
import type { TicketPhase } from "@/lib/types"

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: "Niski", color: "#6B7280" },
  medium: { label: "Sredni", color: "#3B82F6" },
  high: { label: "Wysoki", color: "#F59E0B" },
  urgent: { label: "Pilny", color: "#EF4444" },
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  open: { label: "Otwarty", color: "var(--primary)" },
  resolved: { label: "Rozwiazany", color: "#22C55E" },
  closed: { label: "Zamkniety", color: "#6B7280" },
}

export function ServiceTicketDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useServiceTicket(id)
  const deleteMutation = useDeleteServiceTicket()
  const changePhaseMutation = useChangeTicketPhase()
  const resolveMutation = useResolveTicket()
  const closeMutation = useCloseTicket()

  const ticket = data?.data
  const { data: ticketTypeData } = useTicketType(ticket?.ticket_type?.id)
  const phases = ticketTypeData?.data?.phases ?? []
  const { data: relatedData } = useEntityRelated("ServiceTicket", id)
  const related = relatedData?.data
  const { data: equipmentData } = useTicketEquipment(id)
  const { data: allEquipmentData } = useEquipment()
  const addEquipmentMutation = useAddTicketEquipment()
  const removeEquipmentMutation = useRemoveTicketEquipment()
  const [showEquipmentPicker, setShowEquipmentPicker] = useState(false)
  const [showWorkerPicker, setShowWorkerPicker] = useState(false)
  const [showPartForm, setShowPartForm] = useState(false)
  const [partName, setPartName] = useState("")
  const [partQty, setPartQty] = useState("1")
  const [partPrice, setPartPrice] = useState("")

  const linkedEquipment = equipmentData?.data ?? []
  const linkedIds = new Set(linkedEquipment.map(e => e.id))
  const availableEquipment = (allEquipmentData?.data ?? []).filter(e => !linkedIds.has(e.id))

  // Workers
  const { data: workersData } = useTicketWorkers(id)
  const addWorkerMutation = useAddTicketWorker()
  const removeWorkerMutation = useRemoveTicketWorker()
  const workers = workersData?.data ?? []

  // Time Entries
  const { data: timeEntriesData } = useTicketTimeEntries(id)
  const createTimeEntryMutation = useCreateTimeEntry()
  const stopTimeEntryMutation = useStopTimeEntry()
  const deleteTimeEntryMutation = useDeleteTimeEntry()
  const timeEntries = timeEntriesData?.data ?? []
  const runningEntry = timeEntries.find(e => !e.ended_at)

  // Parts
  const { data: partsData } = useTicketParts(id)
  const addPartMutation = useAddTicketPart()
  const deletePartMutation = useDeleteTicketPart()

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac to zgloszenie?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Zgloszenie usuniete")
    navigate("/service")
  }

  const handlePhaseClick = async (phase: TicketPhase) => {
    if (!id || !ticket || ticket.status === "closed") return
    if (phase.id === ticket.phase.id) return

    try {
      await changePhaseMutation.mutateAsync({ id, phase_id: phase.id })
      if (phase.is_resolved) toast.success("Zgloszenie rozwiazane!")
      else if (phase.is_closed) toast.success("Zgloszenie zamkniete")
      else toast.success(`Etap zmieniony na: ${phase.name}`)
    } catch {
      toast.error("Nie udalo sie zmienic etapu")
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!ticket) {
    return <div className="text-muted-foreground">Nie znaleziono zgloszenia</div>
  }

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open
  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/service")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <RecordNavigation entityType="service_tickets" currentId={id} basePath="/service" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="page-title">{ticket.title}</h1>
              <Badge variant="outline" className="text-xs" style={{ borderColor: statusConfig.color, color: statusConfig.color }}>
                {statusConfig.label}
              </Badge>
              <Badge variant="outline" className="text-[10px]" style={{ borderColor: priorityConfig.color, color: priorityConfig.color }}>
                {priorityConfig.label}
              </Badge>
            </div>
            {ticket.company && (
              <p className="text-sm text-muted-foreground">
                <Building2 size={12} className="inline mr-1" />{ticket.company.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ticket.status === "open" && (
            <>
              <Button variant="outline" size="sm" onClick={() => resolveMutation.mutateAsync({ id: id! }).then(() => toast.success("Zgloszenie rozwiazane!"))}>
                <CheckCircle2 size={14} /> Rozwiaz
              </Button>
              <Button variant="outline" size="sm" onClick={() => closeMutation.mutateAsync(id!).then(() => toast.success("Zgloszenie zamkniete"))}>
                <XCircle size={14} /> Zamknij
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => {
            window.open(`/api/v1/service_tickets/${id}/pdf`, "_blank")
          }}>
            <FileText size={14} /> Raport PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/service/${id}/edit`)}>
            <Pencil size={14} /> {t("common.edit")}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Phase Stepper */}
      {phases.length > 0 && (
        <div className="flex gap-1">
          {phases.filter(p => !p.is_resolved && !p.is_closed).map((phase) => {
            const currentIdx = phases.findIndex(p => p.id === ticket.phase.id)
            const phaseIdx = phases.findIndex(p => p.id === phase.id)
            const isActive = phase.id === ticket.phase.id
            const isPassed = phaseIdx < currentIdx

            return (
              <button
                key={phase.id}
                onClick={() => handlePhaseClick(phase)}
                disabled={ticket.status === "closed"}
                className="flex-1 rounded-md py-2 px-3 text-center text-xs font-medium transition-all disabled:cursor-not-allowed"
                style={{
                  backgroundColor: isActive ? phase.color : isPassed ? `${phase.color}33` : "var(--muted)",
                  color: isActive ? "#fff" : isPassed ? phase.color : "var(--text-muted)",
                  borderLeft: isActive ? `3px solid ${phase.color}` : "none",
                }}
              >
                {phase.name}
              </button>
            )
          })}
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="flex-wrap">
              <TabsTrigger value="overview">Przeglad</TabsTrigger>
              <TabsTrigger value="notes">Notatki{(related?.notes?.count ?? 0) > 0 && <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold bg-muted text-muted-foreground">{related?.notes?.count}</span>}</TabsTrigger>
              <TabsTrigger value="activities">Aktywnosci</TabsTrigger>
              <TabsTrigger value="workers">Pracownicy ({workers.length})</TabsTrigger>
              <TabsTrigger value="time">Czas pracy</TabsTrigger>
              <TabsTrigger value="parts">Czesci ({(partsData?.data ?? []).length})</TabsTrigger>
              <TabsTrigger value="equipment">Urzadzenia ({linkedEquipment.length})</TabsTrigger>
              <TabsTrigger value="history">Historia</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {ticket.description && (
                <div className="surface-card p-4 space-y-2">
                  <h2 className="text-sm font-semibold text-foreground">Opis</h2>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{ticket.description}</p>
                </div>
              )}

              {ticket.resolution_notes && (
                <div className="surface-card p-4 space-y-2 bg-green-500/5">
                  <h2 className="text-sm font-semibold text-green-500">Notatki rozwiazania</h2>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{ticket.resolution_notes}</p>
                </div>
              )}

              {/* Info grid */}
              <div className="surface-card p-4 space-y-3">
                <h2 className="text-sm font-semibold text-foreground">Szczegoly</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Czas pracy</span>
                    <p className="font-mono text-foreground">
                      {ticket.time_entries_summary?.total_hours ?? ticket.total_hours ?? 0}h
                      {ticket.time_entries_summary?.running_count > 0 && (
                        <span className="ml-1.5 text-xs font-normal text-primary">
                          ({ticket.time_entries_summary.running_count} aktywny)
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pracownicy</span>
                    <p className="text-foreground">{workers.length}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Czesci / Materialy</span>
                    <p className="text-foreground">
                      {ticket.parts_summary?.total_items ?? 0} szt.
                      {parseFloat(String(ticket.parts_summary?.total_cost ?? 0)) > 0 && (
                        <span className="ml-1.5 font-mono text-xs">({parseFloat(String(ticket.parts_summary.total_cost)).toFixed(2)} zl)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Dni w etapie</span>
                    <p className="text-foreground">{ticket.days_in_phase}</p>
                  </div>
                  {ticket.contact && (
                    <div>
                      <span className="text-muted-foreground">Osoba kontaktowa</span>
                      <p className="text-foreground">{ticket.contact.first_name} {ticket.contact.last_name}</p>
                    </div>
                  )}
                  {ticket.resolved_at && (
                    <div>
                      <span className="text-muted-foreground">Rozwiazano</span>
                      <p className="text-foreground">{formatDate(ticket.resolved_at)}</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="mt-4">
              <NotesTab entityType="ServiceTicket" entityId={ticket.id} />
            </TabsContent>

            <TabsContent value="activities" className="mt-4">
              <ActivityTimeline entityType="ServiceTicket" entityId={ticket.id} />
            </TabsContent>

            {/* Workers Tab */}
            <TabsContent value="workers" className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  <Users size={14} className="inline mr-1.5" />
                  Przypisani pracownicy
                </h3>
                <Button variant="outline" size="sm" onClick={() => setShowWorkerPicker(!showWorkerPicker)}>
                  <Plus size={14} /> Przypisz
                </Button>
              </div>
              {showWorkerPicker && (
                <div className="surface-card p-3 space-y-2 bg-muted">
                  <p className="text-xs font-medium text-muted-foreground">Wybierz pracownika (jako technika):</p>
                  <div className="flex gap-2">
                    <input
                      id="worker-user-id"
                      className="flex-1 rounded-md border border-input bg-transparent px-3 py-1.5 text-sm outline-none text-foreground"
                      placeholder="ID uzytkownika"
                    />
                    <Button size="sm" onClick={async () => {
                      const input = document.getElementById("worker-user-id") as HTMLInputElement
                      if (!input?.value || !id) return
                      await addWorkerMutation.mutateAsync({ ticketId: id, user_id: input.value })
                      toast.success("Pracownik przypisany")
                      setShowWorkerPicker(false)
                    }}>
                      Dodaj
                    </Button>
                  </div>
                </div>
              )}
              {workers.length === 0 ? (
                <p className="text-center text-sm py-8 text-muted-foreground">Brak przypisanych pracownikow</p>
              ) : (
                <div className="space-y-2">
                  {workers.map((w) => (
                    <div key={w.id} className="flex items-center gap-3 surface-card px-4 py-3">
                      <User size={16} className="text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{w.user.first_name} {w.user.last_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {w.role === "lead" ? "Prowadzacy" : w.role === "helper" ? "Pomocnik" : "Technik"}
                          {w.hourly_rate ? ` \u00B7 ${w.hourly_rate} zl/h` : ""}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-[10px]">{w.role}</Badge>
                      <button
                        onClick={async () => {
                          await removeWorkerMutation.mutateAsync({ ticketId: id!, userId: w.user.id })
                          toast.success("Pracownik usuniety")
                        }}
                        className="rounded-md p-1 transition-colors hover:bg-muted/30 text-muted-foreground"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Time Entries Tab */}
            <TabsContent value="time" className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  <Clock size={14} className="inline mr-1.5" />
                  Czas pracy
                </h3>
                {runningEntry ? (
                  <Button variant="outline" size="sm" className="text-red-600" onClick={async () => {
                    await stopTimeEntryMutation.mutateAsync({ ticketId: id!, entryId: runningEntry.id })
                    toast.success("Timer zatrzymany")
                  }}>
                    <Square size={14} /> Stop
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={async () => {
                    await createTimeEntryMutation.mutateAsync({ ticketId: id! })
                    toast.success("Timer uruchomiony")
                  }}>
                    <Play size={14} /> Start
                  </Button>
                )}
              </div>

              {timeEntries.length === 0 ? (
                <p className="text-center text-sm py-8 text-muted-foreground">Brak wpisow czasu pracy</p>
              ) : (
                <div className="space-y-2">
                  {timeEntries.map((entry) => {
                    const isRunning = !entry.ended_at
                    const mins = entry.duration_minutes ?? 0
                    const hours = Math.floor(mins / 60)
                    const remainMins = mins % 60

                    return (
                      <div key={entry.id} className={`flex items-center gap-3 surface-card px-4 py-3 ${isRunning ? "border-primary bg-blue-600/5" : ""}`}>
                        {isRunning ? (
                          <div className="h-2.5 w-2.5 rounded-full animate-pulse bg-primary" />
                        ) : (
                          <Clock size={16} className="text-muted-foreground" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {isRunning ? "W trakcie..." : `${hours}h ${remainMins}min`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {entry.user.first_name} {entry.user.last_name} &middot; {formatDate(entry.started_at)}
                            {entry.description ? ` \u00B7 ${entry.description}` : ""}
                          </p>
                        </div>
                        {entry.billable && <Badge variant="secondary" className="text-[10px]">Platne</Badge>}
                        {isRunning ? (
                          <button
                            onClick={async () => {
                              await stopTimeEntryMutation.mutateAsync({ ticketId: id!, entryId: entry.id })
                              toast.success("Timer zatrzymany")
                            }}
                            className="rounded-md p-1.5 text-red-600 transition-colors hover:bg-red-50"
                          >
                            <Square size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={async () => {
                              await deleteTimeEntryMutation.mutateAsync({ ticketId: id!, entryId: entry.id })
                              toast.success("Wpis usuniety")
                            }}
                            className="rounded-md p-1 transition-colors hover:bg-muted/30 text-muted-foreground"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </TabsContent>

            {/* Parts Tab */}
            <TabsContent value="parts" className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  <Package size={14} className="inline mr-1.5" />
                  Uzyte czesci i materialy
                </h3>
                <Button variant="outline" size="sm" onClick={() => setShowPartForm(!showPartForm)}>
                  <Plus size={14} /> Dodaj
                </Button>
              </div>

              {showPartForm && (
                <div className="surface-card p-3 space-y-2 bg-muted">
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      value={partName}
                      onChange={(e) => setPartName(e.target.value)}
                      className="col-span-3 rounded-md border border-input bg-transparent px-3 py-1.5 text-sm outline-none text-foreground"
                      placeholder="Nazwa czesci"
                    />
                    <input
                      value={partQty}
                      onChange={(e) => setPartQty(e.target.value)}
                      type="number"
                      className="rounded-md border border-input bg-transparent px-3 py-1.5 text-sm outline-none text-foreground"
                      placeholder="Ilosc"
                    />
                    <input
                      value={partPrice}
                      onChange={(e) => setPartPrice(e.target.value)}
                      type="number"
                      step="0.01"
                      className="rounded-md border border-input bg-transparent px-3 py-1.5 text-sm outline-none text-foreground"
                      placeholder="Cena jedn. (zl)"
                    />
                    <Button size="sm" onClick={async () => {
                      if (!partName.trim() || !id) return
                      await addPartMutation.mutateAsync({
                        ticketId: id,
                        name: partName.trim(),
                        quantity: parseFloat(partQty) || 1,
                        unit_price: partPrice ? parseFloat(partPrice) : undefined,
                      })
                      toast.success("Czesc dodana")
                      setPartName("")
                      setPartQty("1")
                      setPartPrice("")
                      setShowPartForm(false)
                    }}>
                      Dodaj
                    </Button>
                  </div>
                </div>
              )}

              {(partsData?.data ?? []).length === 0 ? (
                <p className="text-center text-sm py-8 text-muted-foreground">Brak uzytych czesci</p>
              ) : (
                <div className="space-y-2">
                  {(partsData?.data ?? []).map((part) => (
                    <div key={part.id} className="flex items-center gap-3 surface-card px-4 py-3">
                      <Package size={16} className="text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{part.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {part.quantity} {part.unit}
                          {part.unit_price ? ` \u00D7 ${part.unit_price} zl` : ""}
                          {part.notes ? ` \u00B7 ${part.notes}` : ""}
                        </p>
                      </div>
                      {part.total_price != null && (
                        <span className="text-sm font-medium text-foreground">
                          {parseFloat(String(part.total_price)).toFixed(2)} zl
                        </span>
                      )}
                      <button
                        onClick={async () => {
                          await deletePartMutation.mutateAsync({ ticketId: id!, partId: part.id })
                          toast.success("Czesc usunieta")
                        }}
                        className="rounded-md p-1 transition-colors hover:bg-muted/30 text-muted-foreground"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  {/* Parts total */}
                  {(partsData?.data ?? []).some(p => p.total_price != null) && (
                    <div className="flex justify-end pt-1">
                      <span className="text-sm font-semibold text-foreground">
                        Razem: {(partsData?.data ?? []).reduce((sum, p) => sum + (parseFloat(String(p.total_price)) || 0), 0).toFixed(2)} zl
                      </span>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="equipment" className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  <Shield size={14} className="inline mr-1.5" />
                  Powiazane urzadzenia
                </h3>
                <Button variant="outline" size="sm" onClick={() => setShowEquipmentPicker(!showEquipmentPicker)}>
                  <Plus size={14} /> Dodaj
                </Button>
              </div>

              {showEquipmentPicker && (
                <div className="surface-card p-3 space-y-2 bg-muted">
                  <p className="text-xs font-medium text-muted-foreground">Wybierz urzadzenie:</p>
                  {availableEquipment.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Brak dostepnych urzadzen</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {availableEquipment.map((eq) => (
                        <button
                          key={eq.id}
                          onClick={async () => {
                            await addEquipmentMutation.mutateAsync({ ticketId: id!, equipment_id: eq.id })
                            toast.success(`Dodano: ${eq.name}`)
                            setShowEquipmentPicker(false)
                          }}
                          className="flex items-center gap-2 w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted/30 text-foreground"
                        >
                          <Shield size={14} className="text-muted-foreground" />
                          <span className="flex-1">{eq.name}</span>
                          {eq.serial_number && <span className="text-xs font-mono text-muted-foreground">{eq.serial_number}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {linkedEquipment.length === 0 ? (
                <p className="text-center text-sm py-8 text-muted-foreground">Brak powiazanych urzadzen</p>
              ) : (
                <div className="space-y-2">
                  {linkedEquipment.map((eq) => (
                    <div
                      key={eq.id}
                      className="flex items-center gap-3 surface-card px-4 py-3 transition-colors hover:bg-muted/30"
                    >
                      <Shield size={16} className="text-muted-foreground" />
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/equipment/${eq.id}`)}
                      >
                        <p className="text-sm font-medium text-foreground">{eq.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {[eq.manufacturer, eq.model_name, eq.serial_number].filter(Boolean).join(" \u00B7 ") || "\u2014"}
                        </p>
                      </div>
                      {eq.category && <Badge variant="secondary" className="text-[10px]">{eq.category}</Badge>}
                      <button
                        onClick={async () => {
                          await removeEquipmentMutation.mutateAsync({ ticketId: id!, equipmentId: eq.id })
                          toast.success("Urzadzenie odlaczone")
                        }}
                        className="rounded-md p-1 transition-colors hover:bg-muted/30 text-muted-foreground"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <div className="space-y-3">
                {ticket.phase_history.length === 0 ? (
                  <p className="text-center text-sm py-8 text-muted-foreground">Brak historii</p>
                ) : (
                  ticket.phase_history.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 surface-card p-3">
                      <Clock size={16} className="mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm text-foreground">
                          {entry.from_phase
                            ? <>{entry.from_phase.name} &rarr; <strong>{entry.to_phase.name}</strong></>
                            : <>Utworzono w etapie: <strong>{entry.to_phase.name}</strong></>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {entry.user && `${entry.user.first_name} ${entry.user.last_name} \u00B7 `}
                          {formatDate(entry.created_at)}
                          {entry.duration_seconds != null && entry.duration_seconds > 0 && (
                            <> &middot; {Math.round(entry.duration_seconds / 86400)} dni w poprzednim etapie</>
                          )}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="surface-card p-4 space-y-3">
            <div>
              <span className="text-xs text-muted-foreground">Typ serwisu</span>
              <p className="text-sm text-foreground">{ticket.ticket_type.name}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Etap</span>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ticket.phase.color }} />
                <p className="text-sm text-foreground">{ticket.phase.name}</p>
              </div>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Priorytet</span>
              <Badge variant="outline" className="mt-0.5 text-[10px]" style={{ borderColor: priorityConfig.color, color: priorityConfig.color }}>
                {priorityConfig.label}
              </Badge>
            </div>
            {ticket.assigned_to && (
              <div>
                <span className="text-xs text-muted-foreground">Przypisany</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <User size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{ticket.assigned_to.first_name} {ticket.assigned_to.last_name}</p>
                </div>
              </div>
            )}
            {ticket.owner && (
              <div>
                <span className="text-xs text-muted-foreground">Opiekun</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <User size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{ticket.owner.first_name} {ticket.owner.last_name}</p>
                </div>
              </div>
            )}
            <div>
              <span className="text-xs text-muted-foreground">Dni w etapie</span>
              <p className="text-sm text-foreground">{ticket.days_in_phase}</p>
            </div>
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">Utworzono</span>
              <p className="text-sm text-foreground">{formatDate(ticket.created_at)}</p>
            </div>
            {ticket.tags.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Tagi</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {ticket.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
